import type { EmployeeInfoContext } from '~/pages/employees/[employee_id]/index.vue'
import type { PolicyResponse } from '~/types'
export type EmployeesPolicyStatus = 'active' | 'pending' | 'inactive-policy' | 'deactivated' | 'queued'
export type StaffRemovalResponse = {
	total_staff_to_be_deactivated: number
	current_staff: number
	total_staff_to_remain: number
}
export type StaffUploadResponse = {
	count: {
		new_staff_added: number
		current_staff: number
		total_staff_added: number
	}
}
export default function useEmployeesApi() {
	const allEmployees = async <T = IRequestResponse<PaginationContext<EmployeesResponse[]>>>(
		query?: { is_active?: boolean; is_export?: boolean; cacheTime?: number; [id: string]: unknown },
		downloadFile?: boolean
	) => {
		let payloadQuery = {}

		if (query) {
			const { cacheTime, ...rest } = query
			payloadQuery = rest
		}
		const { data } = await useAPI<T>('/customers/employees2', {
			query: payloadQuery
		})
		if (downloadFile) {
			return data.value
		}
		// @ts-ignore
		return data.value?.data ?? null
	}

	const getStaffBio = async (employee_id: string) => {
		const { data } = await useAPI<IRequestResponse<EmployeeInfoContext>>(`/customers/${employee_id}/employee`)
		return data.value
	}
	const getEmployeeList = async (query: { purchase_id: string; [id: string]: unknown }) => {
		const { data } = await useAPI<IRequestResponse<PaginationContext<EmployeesResponse[]>>>(
			'/customers/employees-list',
			{ query }
		)
		return data.value
	}
	const employeePlans = async (employee_id: string, filter?: Record<string, unknown>) => {
		const { data } = await useAPI<IRequestResponse<PaginationContext<PolicyResponse[]>>>(
			`/customers/${employee_id}/plans`,
			{ query: filter }
		)

		return data.value?.data ?? null
	}
	const employeeMetrics = async () => {
		const { data } = await useAPI<IRequestResponse<EmployeeMetricsResponse>>('/customers/employee-summary')

		return data.value
	}
	const terminateEmployee = async (customer_id: string) => {
		const { data } = await useAPI<IRequestResponse<unknown>>(`/customers/${customer_id}/terminate`, {
			method: 'PUT'
		})

		return data.value
	}
	const terminateEmployees = async (payload: { customer_ids: string[]; password: string }) => {
		const { data } = await useAPI<IRequestResponse<unknown>>('/customers/employees/batch-terminate', {
			method: 'PUT',
			body: {
				employee_ids: payload.customer_ids,
				password: payload.password
			}
		})

		return data.value
	}
	const downloadEmployeesTemplate = async (
		query: { name?: 'terminate-employee' | 'upload-employee' } = { name: 'upload-employee' }
	) => {
		const { data } = await useAPI<Blob>('/batch/download-employee-template', { query })
		return data
	}
	const uploadEmployeeRecord = async <T = Blob>(type: 'batch' | 'table', payload: Partial<{ file: FormData }>) => {
		let endpoint = 'batch/upload-employees'
		if (type !== 'batch') {
			endpoint = ''
		}
		const { data } = await useAPI<T>(endpoint, { body: payload })
		return data
	}
	const customerMetrics = async (employee_id: string) => {
		const { data } = await useAPI<
			IRequestResponse<{
				totalStaffPendingPolicyCount: number
				totalStaffPolicyCount: number
				totalDeactivatedPolicyCount: number
				totalQueuedPolicyCount: number
			}>
		>(`/customers/${employee_id}/metrics`)
		return data.value
	}
	const computeReactivation = async (payload: { customer_id: string; purchase_id: string }) => {
		const { data } = await useAPI<IRequestResponse<EmployeesReactivateResponse>>(
			'customers/activate-terminated-employee-summary',
			{
				method: 'POST',
				body: payload
			}
		)
		return data.value
	}
	const reactivateEmployee = async (
		payload: { customer_id: string; purchase_id: string; temp_id: string } & PurchaseContext
	) => {
		const { data } = await useAPI<IRequestResponse<EmployeesReactivateResponse>>(
			'customers/activate-terminated-employee',
			{
				method: 'POST',
				body: payload
			}
		)
		return data.value
	}
	const getInactivePendingPolicies = async (customer_id: string) => {
		const { data } = await useAPI<IRequestResponse<PaginationContext<PolicyResponse[]>>>(
			`inactive-pending-policies/employees/${customer_id}`
		)
		return data.value?.data ?? null
	}
	const removeEmployee = async (
		method: 'batch' | 'manual',
		payload: Partial<{
			file: FormData
			instant_removal: boolean
			preview?: boolean
			employee_ids?: string[]
		}>
	) => {
		const { employee_ids, preview, instant_removal, file } = payload
		if (method === 'batch') {
			const response = await useAPI<IRequestResponse<StaffRemovalResponse>>('batch/remove-employees-policies', {
				body: file,
				query: {
					is_preview: Boolean(preview),
					is_immediate: Boolean(instant_removal)
				},
				method: 'DELETE'
			})
			return response.data
		}
		const response = await useAPI<IRequestResponse<StaffRemovalResponse>>('customers/employees/remove', {
			body: { employee_ids },
			query: {
				is_preview: Boolean(preview),
				is_immediate: Boolean(instant_removal)
			},
			method: 'DELETE'
		})
		return response.data
	}
	const uploadEmployee = async <R = StaffUploadResponse>(
		method: 'batch' | 'manual',
		payload: Partial<{
			file: FormData
			employee: {
				first_name: string
				last_name: string
				email: string
				phone: string
			}[]
			preview: boolean
		}>
	) => {
		const { preview, file, employee } = payload
		if (method === 'batch') {
			const response = await useAPI<IRequestResponse<R>>('batch/upload-employees', {
				body: file,
				method: 'POST',
				query: {
					is_preview: Boolean(preview)
				}
			})
			return response.data
		}
		const response = await useAPI<IRequestResponse<R>>('customers/employees', {
			body: { employees: employee },
			method: 'POST',
			query: {
				is_preview: Boolean(preview)
			}
		})
		return response.data
	}

	const deactivateEmployeePolicies = async (
		method: 'batch' | 'manual',
		payload: Partial<{
			file: FormData
			preview: boolean
			instant_deactivation: boolean
			employee_ids?: string[]
			policy_ids?: string[]
			password: string
		}>
	) => {
		const { preview, file, instant_deactivation, employee_ids, policy_ids, password } = payload
		if (method === 'batch') {
			const response = await useAPI<IRequestResponse<StaffRemovalResponse>>('batch/remove-employees-policies', {
				body: file,
				method: 'DELETE',
				query: {
					is_preview: Boolean(preview),
					is_immediate: Boolean(instant_deactivation)
				}
			})
			return response.data
		}
		const response = await useAPI<IRequestResponse<StaffRemovalResponse>>('customers/employees/remove-policies', {
			body: { employee_ids, policy_ids, password },
			method: 'DELETE',
			query: {
				is_preview: Boolean(preview),
				is_immediate: Boolean(instant_deactivation)
			}
		})
		return response.data
	}

	return {
		removeEmployee,
		getStaffBio,
		uploadEmployee,
		deactivateEmployeePolicies,
		uploadEmployeeRecord,
		allEmployees,
		employeePlans,
		employeeMetrics,
		getEmployeeList,
		terminateEmployee,
		terminateEmployees,
		downloadEmployeesTemplate,
		customerMetrics,
		computeReactivation,
		reactivateEmployee,
		getInactivePendingPolicies
	}
}
